/* eslint-disable react/prop-types */
/* eslint-disable react/display-name */
/* eslint-disable react-hooks/rules-of-hooks */
import React, {
  forwardRef,
  InputHTMLAttributes,
  useEffect,
  useState,
  Dispatch,
  SetStateAction,
} from 'react';
import { FieldValues, UseFormRegister } from 'react-hook-form';
import { Warning, LockIcon, Icon } from '..';
import { Search } from './Search';
import { IsolatedSearch } from './IsolatedSearch';
import { MultiSearch } from './Multisearch';
import ErrorMessage from '../error-message/error-message';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  withLabel?: boolean;
  labelType?: 'static' | 'floating';
  register?: UseFormRegister<FieldValues>;
  errors?: any;
  variant:
    | 'default'
    | 'search'
    | 'search-isolated'
    | 'range-slider'
    | 'uploader'
    | 'input-isolated'
    | 'multi-search';
  symbol?: any;
  isValid?: boolean;
  errorMessage?: string;
  searchOptions?: string[];
  positioning?: 'relative' | 'absolute';
  setSelectedOptions?: Dispatch<SetStateAction<string[]>>;
  selectedOptions?: string[];
  multisearchLabel?: string;
  rangeBoxLabel?: string;
  rangeWholeNumber?: boolean;
  disableRangeTextMax?: boolean;
  disableRangeTextMin?: boolean;
  customPlaceholder?: string;
  rangeSliderColor?: string;
  noRangeTextbox?: boolean;
  rangeSliderThumbValue?: string;
  maxListHeight?: string;
}

/* Styles for default input */
const inputStyle = `apperance-none px-0 border-b border-thryv-gray-light-500 
text-thryv-black-500 font-open-sans text-form-text font-normal 
focus:border-b-2 focus:border-thryv-black-500 focus:outline-none 
focus:ring-0 transition duration-300 ease-in-out focus:placeholder-transparent 
disabled:placeholder-thryv-gray-medium-500 disabled:border-b disabled:border-thryv-gray-light-500 
disabled:text-thryv-gray-medium-500 mt-1 bg-transparent`;

/* Styles for search input */
const floatingLabelStyle = `absolute left-0 -top-3.5 capitalize text-thryv-steel 
font-open-sans text-label-text peer-placeholder-shown:text-form-text peer-placeholder-shown:text-thryv-steel 
peer-placeholder-shown:top-1.5 transition-all peer-focus:-top-3.5 peer-focus:text-label-text 
peer-focus:placeholder-transparent peer-focus:placeholder-opacity-0 peer-focus:placeholder-white"`;

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      disabled,
      errors,
      labelType,
      name,
      onChange,
      onBlur,
      placeholder,
      register,
      type,
      variant,
      withLabel,
      min,
      max,
      value,
      step,
      readOnly,
      symbol,
      isValid = true,
      errorMessage,
      searchOptions,
      positioning,
      setSelectedOptions,
      selectedOptions,
      multisearchLabel,
      required,
      rangeBoxLabel,
      rangeWholeNumber,
      disableRangeTextMax,
      disableRangeTextMin,
      customPlaceholder,
      rangeSliderColor,
      noRangeTextbox,
      rangeSliderThumbValue,
      maxListHeight,
      ...rest
    },
    ref
  ) => {
    const [focus, setFocus] = useState<boolean>(false);

    /* Rendering default input */
    const renderDefaultInput =
      variant === 'default' ? (
        <>
          <div className={`flex justify-start items-center`}>
            {(!disabled && withLabel && !labelType) ||
              (labelType === 'static' ? (
                <label
                  htmlFor={name}
                  className={`
                  absolute left-0 -top-3.5 capitalize font-open-sans 
                  ${
                    (errors && name && errors[name] && errors[name].message) ||
                    !isValid
                      ? 'text-utility-error-danger peer-focus:text-utility-error-danger text-label-text'
                      : 'text-thryv-steel text-label-text'
                  }
                  ${
                    readOnly &&
                    'text-thryv-gray-medium-500 peer-focus:text-thryv-gray-medium-500'
                  }`}
                >
                  {placeholder}
                  {required && <span>*</span>}
                </label>
              ) : null)}
            <input
              placeholder={`${
                customPlaceholder
                  ? `${customPlaceholder}`
                  : `Enter ${placeholder}...`
              } `}
              name={name}
              type={type}
              className={`${withLabel ? 'peer' : ''} ${inputStyle} ${
                labelType === 'floating'
                  ? 'placeholder-opacity-0 placeholder-transparent pb-1'
                  : 'placeholder-thryv-gray-medium-500 pb-1'
              } valid:border-b invalid:border-b bg-none ${
                ((errors && name && errors[name] && errors[name].message) ||
                  !isValid) &&
                'border-utility-error-danger focus:border-b-2 focus:border-utility-error-danger peer-focus:border-utility-error-danger peer-focus:border-b-2'
              } ${
                readOnly &&
                'text-thryv-gray-medium-500 border-thryv-gray-light-500 focus:border-b peer-focus:border-b peer-focus:border-thryv-gray-light-500 peer-focus:text-thryv-gray-light-500 focus:border-thryv-gray-light-500 focus:text-thryv-gray-light-500'
              } ${className || ''}`}
              onChange={(e: any) => onChange && onChange(e)}
              onBlur={onBlur && onBlur}
              {...(register && {
                ...register(`${name}`),
              })}
              readOnly={readOnly || false}
              value={value}
              min={min}
              max={max}
              step={step}
              disabled={disabled}
              {...rest}
            />
            {((errors && name && errors[name] && errors[name].message) ||
              !isValid) && <Warning className="-m-4" />}
            {withLabel && labelType === 'floating' && !disabled ? (
              <label
                htmlFor={name}
                className={`pointer-events-none ${floatingLabelStyle} ${
                  (errors && name && errors[name] && errors[name].message) ||
                  !isValid
                    ? 'text-utility-error-danger peer-focus:text-utility-error-danger'
                    : 'text-thryv-black-500 peer-focus:text-thryv-black-500'
                } ${
                  readOnly &&
                  'text-thryv-gray-medium-500 border-thryv-gray-light-500 focus:border-b peer-focus:border-b peer-focus:border-thryv-gray-light-500 peer-focus:text-thryv-gray-light-500 focus:border-thryv-gray-light-500 focus:text-thryv-gray-light-500'
                } `}
              >
                {placeholder}
                {required && <span>*</span>}
              </label>
            ) : null}
          </div>
          {errors && name && errors[name] && errors[name].message && (
            <ErrorMessage errorMessage={errors[name].message} />
          )}
          {(errors || !isValid) && errorMessage && (
            <ErrorMessage errorMessage={errorMessage} />
          )}
        </>
      ) : null;

    /* Rendering isolated input */
    const renderInputIsolated =
      variant === 'input-isolated' ? (
        <>
          <div
            onClick={() => setFocus(true)}
            onBlur={() => setFocus(false)}
            className={`relative flex justify-between items-center px-0 py-2 border ${
              !focus
                ? ' border-thryv-gray-light-500 placeholder-thryv-steel'
                : 'border-thryv-black-500 ring-1 ring-thryv-black-500'
            } rounded transition-all bg-transparent  ${className || ''} ${
              ((errors && name && errors[name] && errors[name].message) ||
                !isValid) &&
              'border-utility-error-danger focus:border-b-2 peer-focus:border-utility-error-danger ring-utility-error-danger'
            }`}
          >
            {symbol && (
              <span
                className={`${
                  errors && name && errors[name]
                    ? 'text-utility-error-danger'
                    : 'text-thryv-black-500'
                } text-form-text pl-4`}
              >
                {symbol}
              </span>
            )}
            <input
              ref={ref}
              placeholder={placeholder}
              type={type}
              name={name}
              className={`peer appearance-none 
            text-thryv-black-500 text-form-text border-none w-full ring-0 focus:outline-none pl-[4px] py-0 bg-transparent`}
              {...(register && {
                ...register(`${name}`, { required: `${name} is required.` }),
              })}
              readOnly={readOnly || false}
              onChange={onChange}
              value={value}
              min={min}
              max={max}
              step={step}
              disabled={disabled}
              {...rest}
            />
            {((errors && name && errors[name] && errors[name].message) ||
              !isValid) && (
              <div className="mr-[8px]">
                <Icon
                  type="solid"
                  variant="circleExclamation"
                  color="#DF2A2A"
                  height="15px"
                />
              </div>
            )}
          </div>
          {errors && name && errors[name] && errors[name].message && (
            <ErrorMessage
              errorMessage={errors[name].message}
              styles="absolute"
            />
          )}
          {(errors || !isValid) && errorMessage && (
            <ErrorMessage errorMessage={errorMessage} styles="absolute" />
          )}
        </>
      ) : null;

    /* Rendering search input */
    const renderSearchInput =
      variant === 'search' && !labelType ? (
        <>
          <Search
            type={type}
            name={name}
            placeholder={placeholder}
            value={value}
            className={className}
            register={register}
            onChange={onChange}
            onBlur={onBlur}
            searchOptions={searchOptions}
            positioning={positioning}
            errors={errors}
            isValid={isValid}
            min={min}
            max={max}
            step={step}
            disabled={disabled}
            focus={focus}
            setFocus={setFocus}
            maxListHeight={maxListHeight}
            {...rest}
          />
          {errors && name && errors[name] && errors[name].message && (
            <ErrorMessage errorMessage={errors[name].message} />
          )}
          {(errors || !isValid) && errorMessage && (
            <ErrorMessage errorMessage={errorMessage} />
          )}
        </>
      ) : null;

    /* Rendering search input isolated */
    const renderSearchInputIsolated = () => {
      const { style } = rest;
      return variant === 'search-isolated' && !labelType ? (
        <>
          <IsolatedSearch
            type={type}
            name={name}
            placeholder={placeholder}
            value={value}
            className={className}
            register={register}
            onChange={onChange}
            positioning={positioning}
            searchOptions={searchOptions}
            setFocus={setFocus}
            focus={focus}
            style={style}
            errors={errors}
            isValid={isValid}
            onBlur={onBlur}
            min={min}
            max={max}
            step={step}
            disabled={disabled}
            readOnly={readOnly || false}
            maxListHeight={maxListHeight}
            {...rest}
          />
          {errors && name && errors[name] && errors[name].message && (
            <ErrorMessage errorMessage={errors[name].message} />
          )}
          {(errors || !isValid) && errorMessage && (
            <ErrorMessage errorMessage={errorMessage} />
          )}
        </>
      ) : null;
    };

    /* Rendering multi-search input */
    const renderMultiSearchInput =
      variant === 'multi-search' && !labelType ? (
        <>
          <MultiSearch
            type={type}
            name={name}
            placeholder={placeholder}
            value={value}
            className={className}
            register={register}
            searchOptions={searchOptions}
            positioning={positioning}
            setSelectedOptions={setSelectedOptions}
            selectedOptions={selectedOptions}
            withLabel={withLabel}
            multisearchLabel={multisearchLabel}
            errors={errors}
            isValid={isValid}
            min={min}
            max={max}
            step={step}
            disabled={disabled}
            focus={focus}
            setFocus={setFocus}
            maxListHeight={maxListHeight}
            {...rest}
          />
          {errors && name && errors[name] && errors[name].message && (
            <ErrorMessage errorMessage={errors[name].message} />
          )}
          {(errors || !isValid) && errorMessage && (
            <ErrorMessage errorMessage={errorMessage} />
          )}
        </>
      ) : null;

    /* Getting value for range-slider */
    const [ratio, setRatio] = useState<number>(
      (typeof value === 'string' && parseInt(value)) ||
        (typeof value === 'number' && value) ||
        0
    );
    // bubbleDifference is used to help center the value in the range-slider thumb
    const [bubbleDifference, setBubbleDifference] = useState<number>(50);
    // step passed to the range slider step
    const [rangeSliderStep, setRangeSliderStep] = useState<number>(
      !step ? 1 : typeof step === 'string' ? parseInt(step, 10) : step
    );

    useEffect(() => {
      if (
        typeof value === 'string' &&
        (parseInt(value) || parseInt(value) === 0)
      )
        setRatio(parseInt(value));
      if (typeof value === 'number') setRatio(value);
    }, [value]);

    useEffect(() => {
      if (rangeWholeNumber) {
        setRangeSliderStep(
          100 /
            (typeof max === 'string'
              ? parseInt(max, 10)
              : typeof max === 'number'
              ? max
              : 100)
        );
      }
    }, [rangeWholeNumber, max]);

    // handeSetRatio is an event handler that sets the value in the bubble based on the value from the onChange input event
    const handleSetRatio = (e: React.ChangeEvent<HTMLInputElement>) => {
      setRatio(parseFloat(e.target.value));
      onChange && onChange(e);
    };

    // updates ratio and triggers onChange when the number input is manually changed
    const handleManualInput = (e: React.ChangeEvent<HTMLInputElement>) => {
      setRatio(
        e?.target?.value
          ? typeof max === 'string'
            ? (parseInt(e?.target?.value, 10) * 100) / parseInt(max, 10)
            : typeof max === 'number'
            ? (parseInt(e?.target?.value, 10) * 100) / max
            : 0
          : 0
      );
      onChange && onChange(e);
    };

    // These styles are used to color the track and thumb of the range-slider
    const backgroundStyles = {
      background: `${
        disabled
          ? '#cccccc'
          : ratio
          ? `linear-gradient(
        to right, 
       ${rangeSliderColor || '#231f20'},
       ${rangeSliderColor || '#231f20'} ${ratio}%,
        #cccccc ${ratio}%,
        #cccccc 100%
      )`
          : '#cccccc'
      } `,
      '--thumb': `${ratio === 0 ? '#cccccc' : rangeSliderColor || ' #231f20'}`,
      '--left': ` ${ratio >= 100 && rangeWholeNumber ? '5px' : '0'}`,
    };

    // Dynamically setting bubbleDifference to help keep the value centered in the range-slider thumb.
    useEffect(() => {
      if (ratio < 50) {
        const difference = 50 - ratio;
        setBubbleDifference(difference / 50);
      } else if (ratio > 50 && ratio) {
        const difference = ratio - 50;
        setBubbleDifference(-difference / 50);
      } else setBubbleDifference(0);
    }, [ratio]);

    const renderRangeSlider = variant === 'range-slider' && (
      <div className="flex items-center sm:flex-col sm:items-center">
        <div
          className={`w-full relative flex items-baseline ${
            disabled || noRangeTextbox ? 'w-full' : 'w-11/12 sm:w-full'
          } ${disabled ? '' : 'font-open-sans'} `}
        >
          {disabled ? (
            <>
              <LockIcon className="mr-[12px]" />
              <div className="rangeDisabled" style={backgroundStyles}></div>
            </>
          ) : (
            <div
              className={`relative w-full ${
                noRangeTextbox ? '' : 'mr-[30px]'
              } sm:mr-0`}
            >
              <input
                type="range"
                min={0}
                max={
                  rangeWholeNumber
                    ? `${(100).toFixed(
                        typeof max === 'string'
                          ? String(parseInt(max, 10).toFixed(0))?.length
                          : typeof max === 'number'
                          ? String(max.toFixed(0))?.length
                          : 2
                      )}9`
                    : 100
                }
                name={name}
                value={ratio}
                step={rangeSliderStep}
                className={`range focus-visible:ring-4 relative sm:z-10`}
                onChange={handleSetRatio}
                style={backgroundStyles}
                disabled={disabled}
              />
              <div
                className="pointer-events-none  bg-transparent text-white absolute text-[14px] w-[60px] flex justify-center font-open-sans font-semibold sm:z-20"
                style={{
                  left: `${
                    ratio > 100
                      ? `calc((100% - 60px))`
                      : `calc((${ratio}% + ${bubbleDifference} * 30px) - 30px)`
                  }`,
                  bottom: '2px',
                }}
              >
                {rangeSliderThumbValue
                  ? rangeSliderThumbValue
                  : `${Math.round(ratio)}%`}
              </div>
            </div>
          )}
        </div>
        {disabled || noRangeTextbox ? null : (
          <div className="flex flex-col w-max whitespace-nowrap relative items-start sm:z-0 sm:mt-[9px] sm:top-[13px]">
            {(errors && name && errors[name] && errors[name].message) ||
            !isValid ||
            (errors && errorMessage) ? (
              <>
                {errors && name && errors[name] && errors[name].message && (
                  <ErrorMessage
                    errorMessage={errors[name].message}
                    styles="absolute top-[-20px]"
                  />
                )}
                {(errors || !isValid) && errorMessage && (
                  <ErrorMessage
                    errorMessage={errorMessage}
                    styles="absolute top-[-20px]"
                  />
                )}
              </>
            ) : rangeBoxLabel ? (
              <div
                className="flex absolute bottom-full sm:top-[-15px]  text-thryv-steel text-p-sm font-open-sans font-normal"
                aria-roledescription="Range Slider Input Text Box Label"
              >
                {rangeBoxLabel}
              </div>
            ) : null}
            <>
              <div
                onClick={() => setFocus(true)}
                onBlur={() => setFocus(false)}
                className={`relative flex justify-between items-center px-0 py-2 border ${
                  !focus
                    ? ' border-thryv-gray-light-500 placeholder-thryv-steel'
                    : 'border-thryv-black-500 ring-1 ring-thryv-black-500'
                } rounded transition-all bg-transparent w-[120px] ${
                  className || ''
                } ${
                  ((errors && name && errors[name] && errors[name].message) ||
                    !isValid) &&
                  'border-utility-error-danger focus:border-b-2 peer-focus:border-utility-error-danger ring-utility-error-danger'
                }`}
              >
                <span
                  className={`${
                    (errors && name && errors[name]) || !isValid
                      ? 'text-utility-error-danger'
                      : 'text-thryv-black-500'
                  } text-form-text pl-2`}
                >
                  $
                </span>
                <input
                  aria-roledescription="range-slider text-input"
                  ref={ref}
                  placeholder={placeholder}
                  type="number"
                  name={`${name} text-input`}
                  className={`peer appearance-none 
            text-thryv-black-500 text-form-text border-none w-[83px] mr-auto ring-0 focus:outline-none px-[4px] py-0 bg-transparent`}
                  {...(register && {
                    ...register(`${name}`, {
                      required: `${name} is required.`,
                    }),
                  })}
                  readOnly={readOnly || false}
                  onChange={(e) => {
                    handleManualInput(e);
                  }}
                  value={
                    ratio
                      ? rangeWholeNumber
                        ? Math.round(
                            typeof max === 'string'
                              ? (ratio / 100) * parseInt(max, 10)
                              : typeof max === 'number'
                              ? (ratio / 100) * max
                              : 0
                          )
                        : Math.round(
                            (typeof max === 'string'
                              ? (ratio / 100) * parseInt(max, 10)
                              : typeof max === 'number'
                              ? (ratio / 100) * max
                              : 0) * 100
                          ) / 100
                      : ''
                  }
                  min={disableRangeTextMin ? undefined : min}
                  max={disableRangeTextMax ? undefined : max}
                  disabled={disabled}
                  {...rest}
                />
                {((errors && name && errors[name] && errors[name].message) ||
                  !isValid) && (
                  <div className="right-0 absolute">
                    <Icon
                      type="solid"
                      variant="circleExclamation"
                      color="#DF2A2A"
                      height="15px"
                    />
                  </div>
                )}
              </div>
            </>
          </div>
        )}
      </div>
    );

    const renderUploaderInput =
      variant === 'uploader' ? (
        <>
          <input
            id={name}
            disabled={disabled}
            type="file"
            className="opacity-0 pointer-events-none absolute peer"
            name={name}
            accept="image/png, image/jpeg"
            onChange={(e) => onChange && onChange(e)}
            {...(register && { ...register(`${name}`) })}
          />
          <label
            className={`w-[144px] h-8 border-0 bg-none overflow-hidden px-6 py-2.5 peer-focus-visible:ring-4 ${
              disabled ? 'bg-thryv-gray-light-500' : 'bg-thryv-orange-500'
            } rounded ${!disabled && 'cursor-pointer'} ${
              disabled
                ? 'hover:bg-thryv-gray-light-500'
                : 'hover:bg-thryv-orange-700'
            } text-white text-uploader-btn font-open-sans font-semibold capitalize ${
              className || ''
            } transition-all`}
            htmlFor={name}
            aria-hidden="true"
          >
            {placeholder || 'Choose File'}
          </label>
        </>
      ) : null;

    return (
      <>
        <div className="relative">
          {renderDefaultInput}
          {renderSearchInput}
          {renderInputIsolated}
          {renderMultiSearchInput}
        </div>
        {renderSearchInputIsolated()}
        {renderRangeSlider}
        {renderUploaderInput}
      </>
    );
  }
);
